import { eq } from 'drizzle-orm'
import { useRef, useState } from 'react'
import {
	type ActionFunctionArgs,
	Form,
	type LoaderFunctionArgs,
	data,
	useActionData,
	useLoaderData,
} from 'react-router'
import { FloatingLabelField, FloatingSelectField } from '#app/components/forms'
import { Button } from '#app/components/ui/button'
import {
	type OpenroomIdentityAddress,
	type OpenroomIdentityRequest,
	type OpenroomNameAlias,
	callOpenroomIdentity,
} from '#app/services/openroom.server'
import { db } from '#app/utils/db.server'
import { createScreeningApplication } from '#app/utils/screening-db.server'
import { saveStepData } from '#app/utils/screening-db.server'
import { user } from '#db/schema/base'
import { userOpenroomRecord } from '#db/schema/userOpenroomRecord'

type ActionData =
	| {
			success: true
			message: string
			recordId: string
	  }
	| { error: string }
	| null

async function getTenantIdFromHostname(hostname: string): Promise<string> {
	const { tenant } = await import('#db/schema/base')
	const existingTenant = await db.query.tenant.findFirst({
		where: (t, { eq }) => eq(t.hostname, hostname),
	})

	if (existingTenant) {
		return existingTenant.id
	}

	const trueTenant = await db.query.tenant.findFirst({
		where: (t, { eq, or: orFn }) =>
			orFn(
				eq(t.name, 'TrueTenant'),
				eq(t.hostname, 'localhost'),
				eq(t.hostname, 'truetenant.ai'),
			),
	})

	if (!trueTenant) {
		throw new Error(
			'No tenant found for this hostname. Please ensure tenant is configured.',
		)
	}

	return trueTenant.id
}

export async function loader({ request }: LoaderFunctionArgs) {
	try {
		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		const dbUsers = await db.query.user.findMany({
			where: eq(user.tenantId, tenantId),
			columns: {
				id: true,
				name: true,
				email: true,
				username: true,
			},
		})

		const userOptions = dbUsers.map((u) => ({
			label: u.name || u.email || u.username || 'Unknown User',
			value: u.id,
		}))

		return {
			users: userOptions,
		}
	} catch (error) {
		console.error('[Openroom External] Loader error:', error)
		return {
			users: [],
			error: error instanceof Error ? error.message : 'Failed to load data',
		}
	}
}

function parseAddresses(formData: FormData): OpenroomIdentityAddress[] {
	const addresses: OpenroomIdentityAddress[] = []
	let i = 0
	while (true) {
		const street = formData.get(`address_${i}_street`)?.toString()?.trim()
		if (street === undefined || street === '') break
		const city = formData.get(`address_${i}_city`)?.toString()?.trim() ?? ''
		const postalCode =
			formData.get(`address_${i}_postal_code`)?.toString()?.trim() ?? ''
		const province =
			formData.get(`address_${i}_province`)?.toString()?.trim() ?? ''
		const country =
			formData.get(`address_${i}_country`)?.toString()?.trim() ?? 'CA'
		addresses.push({
			address_1: street,
			city,
			postal_code: postalCode,
			province_code: province,
			country_code: country,
		})
		i++
	}
	return addresses
}

function parseAliases(formData: FormData): OpenroomNameAlias[] {
	const aliases: OpenroomNameAlias[] = []
	for (let i = 0; i < 3; i++) {
		const first = formData.get(`alias_${i}_first_name`)?.toString()?.trim()
		const last = formData.get(`alias_${i}_last_name`)?.toString()?.trim()
		if (!first && !last) continue
		aliases.push({
			first_name: first ?? '',
			middle_name:
				formData.get(`alias_${i}_middle_name`)?.toString()?.trim() ?? '',
			last_name: last ?? '',
		})
	}
	return aliases
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	try {
		const userId = formData.get('user')?.toString()
		const firstName = formData.get('first_name')?.toString()?.trim()
		const middleName = formData.get('middle_name')?.toString()?.trim()
		const lastName = formData.get('last_name')?.toString()?.trim()
		const birthDate = formData.get('birth_date')?.toString()?.trim()

		if (!userId) {
			return data({ error: 'User is required' }, { status: 400 })
		}
		if (!firstName) {
			return data({ error: 'First name is required' }, { status: 400 })
		}
		if (!lastName) {
			return data({ error: 'Last name is required' }, { status: 400 })
		}
		if (!birthDate) {
			return data({ error: 'Birth date is required' }, { status: 400 })
		}
		const birthDateRegex = /^\d{4}-\d{2}-\d{2}$/
		if (!birthDateRegex.test(birthDate)) {
			return data({ error: 'Birth date must be YYYY-MM-DD' }, { status: 400 })
		}

		const addresses = parseAddresses(formData)
		if (addresses.length === 0) {
			return data(
				{ error: 'At least one address is required' },
				{ status: 400 },
			)
		}

		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		const dbUser = await db.query.user.findFirst({
			where: eq(user.id, userId),
			columns: { id: true },
		})
		if (!dbUser) {
			return data({ error: 'User not found' }, { status: 404 })
		}

		const screeningApplicationId = await createScreeningApplication({
			userId,
			tenantId,
		})

		const aliases = parseAliases(formData)
		const payload: OpenroomIdentityRequest = {
			name: {
				first_name: firstName,
				middle_name: middleName ?? '',
				last_name: lastName,
			},
			addresses,
			...(aliases.length > 0 && { aliases }),
			birth_date: birthDate,
		}

		const response = await callOpenroomIdentity(payload)

		const matches = response.matches ?? []
		const courtOrderLink =
			matches.length > 0 && typeof matches[0]?.url === 'string'
				? (matches[0].url as string)
				: null

		const existing = await db
			.select()
			.from(userOpenroomRecord)
			.where(
				eq(userOpenroomRecord.screeningApplicationId, screeningApplicationId),
			)
			.limit(1)

		let recordId: string

		if (existing[0]) {
			await db
				.update(userOpenroomRecord)
				.set({
					response: response as Record<string, unknown>,
					courtOrderLink,
					updatedAt: new Date(),
				})
				.where(eq(userOpenroomRecord.id, existing[0].id))
			recordId = existing[0].id
		} else {
			const [inserted] = await db
				.insert(userOpenroomRecord)
				.values({
					userId,
					screeningApplicationId,
					response: response as Record<string, unknown>,
					courtOrderLink,
				})
				.returning({ id: userOpenroomRecord.id })
			if (!inserted?.id) throw new Error('Failed to insert openroom record')
			recordId = inserted.id
		}

		const hasMatches = matches.length > 0
		const stepPayload = {
			openRoomRecordID: recordId,
			courtOrderLink,
			status: hasMatches,
			matches: hasMatches ? 'Found' : 'Not_Found',
			completed: true,
			completedAt: new Date().toISOString(),
		}

		await saveStepData(
			screeningApplicationId,
			'openroom',
			stepPayload,
			tenantId,
		)

		return data({
			success: true,
			message: 'OpenRooms API call completed successfully',
			recordId,
		})
	} catch (error) {
		console.error('[Openroom External] Action error:', error)
		const errorMessage =
			error instanceof Error
				? error.message
				: 'Failed to process OpenRooms request'
		return data({ error: errorMessage }, { status: 500 })
	}
}

function OpenroomForm({
	userOptions,
	actionData,
}: {
	userOptions: { label: string; value: string }[]
	actionData: ActionData
}) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const formRef = useRef<HTMLFormElement>(null)
	const [selectedUser, setSelectedUser] = useState('')
	const [addressCount, setAddressCount] = useState(1)

	const success = actionData && 'success' in actionData && actionData.success
	const error = actionData && 'error' in actionData ? actionData.error : null

	return (
		<>
			{error && (
				<div className="rounded-lg bg-destructive/10 border border-destructive p-4">
					<p className="text-sm text-destructive font-medium">Error</p>
					<p className="text-sm text-destructive/80 mt-1">{error}</p>
				</div>
			)}

			{success && actionData && 'recordId' in actionData && (
				<div className="rounded-lg bg-green-50 border border-green-200 p-4">
					<p className="text-sm text-green-800 font-medium">Success</p>
					<p className="text-sm text-green-700 mt-1">{actionData.message}</p>
					<p className="text-xs text-green-600 mt-1">
						Record ID: {actionData.recordId}
					</p>
				</div>
			)}

			<Form
				key={
					success
						? `done-${actionData && 'recordId' in actionData ? actionData.recordId : ''}`
						: 'form'
				}
				ref={formRef}
				method="post"
				className="space-y-4"
				onSubmit={() => setIsSubmitting(true)}
			>
				<div>
					<FloatingSelectField
						labelProps={{ children: 'User' }}
						value={selectedUser}
						onChange={(value) => setSelectedUser(value)}
						options={userOptions}
					/>
					<input type="hidden" name="user" value={selectedUser} />
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<FloatingLabelField
						labelProps={{ children: 'First Name' }}
						inputProps={{
							name: 'first_name',
							type: 'text',
							required: true,
						}}
					/>
					<FloatingLabelField
						labelProps={{ children: 'Middle Name' }}
						inputProps={{
							name: 'middle_name',
							type: 'text',
						}}
					/>
					<FloatingLabelField
						labelProps={{ children: 'Last Name' }}
						inputProps={{
							name: 'last_name',
							type: 'text',
							required: true,
						}}
					/>
				</div>

				<FloatingLabelField
					labelProps={{ children: 'Birth Date (YYYY-MM-DD)' }}
					inputProps={{
						name: 'birth_date',
						type: 'date',
						required: true,
					}}
				/>

				<div>
					<h3 className="text-sm font-medium mb-2">Addresses</h3>
					{Array.from({ length: addressCount }, (_, i) => (
						<div
							key={i}
							className="border rounded-lg p-4 mb-4 space-y-3 bg-muted/30"
						>
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">
									Address {i + 1}
								</span>
								{addressCount > 1 && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => setAddressCount((c) => Math.max(1, c - 1))}
									>
										Remove
									</Button>
								)}
							</div>
							<FloatingLabelField
								labelProps={{ children: 'Street' }}
								inputProps={{
									name: `address_${i}_street`,
									type: 'text',
									required: i === 0,
								}}
							/>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<FloatingLabelField
									labelProps={{ children: 'City' }}
									inputProps={{
										name: `address_${i}_city`,
										type: 'text',
										required: i === 0,
									}}
								/>
								<FloatingLabelField
									labelProps={{ children: 'Postal Code' }}
									inputProps={{
										name: `address_${i}_postal_code`,
										type: 'text',
										required: i === 0,
									}}
								/>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<FloatingLabelField
									labelProps={{ children: 'Province' }}
									inputProps={{
										name: `address_${i}_province`,
										type: 'text',
										required: i === 0,
									}}
								/>
								<FloatingLabelField
									labelProps={{ children: 'Country' }}
									inputProps={{
										name: `address_${i}_country`,
										type: 'text',
										required: i === 0,
										defaultValue: 'CA',
									}}
								/>
							</div>
						</div>
					))}
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => setAddressCount((c) => c + 1)}
					>
						Add new address
					</Button>
				</div>

				<div>
					<h3 className="text-sm font-medium mb-2">Name aliases (up to 3)</h3>
					{[0, 1, 2].map((i) => (
						<div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
							<FloatingLabelField
								labelProps={{ children: `Alias ${i + 1} First` }}
								inputProps={{
									name: `alias_${i}_first_name`,
									type: 'text',
								}}
							/>
							<FloatingLabelField
								labelProps={{ children: `Alias ${i + 1} Middle` }}
								inputProps={{
									name: `alias_${i}_middle_name`,
									type: 'text',
								}}
							/>
							<FloatingLabelField
								labelProps={{ children: `Alias ${i + 1} Last` }}
								inputProps={{
									name: `alias_${i}_last_name`,
									type: 'text',
								}}
							/>
						</div>
					))}
				</div>

				<Button type="submit" disabled={isSubmitting} className="w-full">
					{isSubmitting ? 'Submitting...' : 'Submit OpenRooms Request'}
				</Button>
			</Form>
		</>
	)
}

export default function OpenroomExternalRoute() {
	const { users: userOptions } = useLoaderData<typeof loader>()
	const actionData = useActionData<ActionData>()

	return (
		<div className="container mx-auto max-w-2xl py-8 px-4">
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">OpenRooms API Form</h1>
					<p className="text-muted-foreground mt-2">
						Submit an OpenRooms court records identity check
					</p>
				</div>

				<OpenroomForm
					userOptions={userOptions}
					actionData={actionData ?? null}
				/>
			</div>
		</div>
	)
}
